import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "theme-ui/jsx-runtime";
import { debounce } from "ts-debounce";
import { useCallback, useEffect, useRef, useState } from "react";
import { Box, Spinner } from "theme-ui";
import { H6 } from "../Headings";
import { Colors, LinkVariants, TextVariants, ThemeLinks } from "../../index";
import { getWindowDimensions, isTouchDevice } from "../../utils";
const CLOSE_TOOLTIP_EVENT = "closeTooltip";
const TOOLTIP_BOX_WIDTH = 450;
const TOOLTIP_ARROW_WIDTH = 50;
const TOOLTIP_ARROW_HEIGHT = 50;
const TooltipContainerStyles = {
    top: {
        left: 0,
        top: "auto",
        bottom: "calc(100% + 16px)"
    },
    bottom: {
        left: 0,
        top: "calc(100% + 16px)"
    },
    right: {
        top: "auto",
        bottom: "-24px",
        left: "calc(100% + 24px)"
    },
    left: {
        top: "auto",
        bottom: "-24px",
        right: "calc(100% + 24px)"
    }
};
const CommonArrowAfterStyles = {
    content: "''",
    position: "absolute",
    width: "20px",
    height: "20px",
    border: "1px solid",
    borderColor: `${Colors.grey}.3`,
    background: "#fff",
    transform: "rotate(45deg)"
};
const TooltipArrowStyles = {
    top: {
        width: `100%`,
        height: `${TOOLTIP_ARROW_HEIGHT / 2}px`,
        left: "0px",
        bottom: "calc(100% - 8px)",
        "&:after": Object.assign(Object.assign({}, CommonArrowAfterStyles), { bottom: "14px", left: "16px", boxShadow: "2px 2px 4px -1px rgb(0 0 0 / 25%)" })
    },
    bottom: {
        width: `100%`,
        height: `${TOOLTIP_ARROW_HEIGHT / 2}px`,
        left: "0px",
        top: "calc(100% - 8px)",
        "&:after": Object.assign(Object.assign({}, CommonArrowAfterStyles), { top: "14px", left: "16px" })
    },
    right: {
        width: `${TOOLTIP_ARROW_WIDTH / 2}px`,
        height: `${TOOLTIP_ARROW_HEIGHT}px`,
        right: `-${TOOLTIP_ARROW_WIDTH / 2}px`,
        bottom: `calc(50% - ${TOOLTIP_ARROW_HEIGHT / 2}px)`,
        "&:after": Object.assign(Object.assign({}, CommonArrowAfterStyles), { top: "15px", left: "14px", boxShadow: "1px 1px 4px -1px rgb(0 0 0 / 25%)" })
    },
    left: {
        width: `${TOOLTIP_ARROW_WIDTH / 2}px`,
        height: "50px",
        left: "-25px",
        top: `calc(50% - ${TOOLTIP_ARROW_HEIGHT / 2}px)`,
        "&:after": Object.assign(Object.assign({}, CommonArrowAfterStyles), { top: "15px", right: "14px", boxShadow: "3px 1px 4px -1px rgb(0 0 0 / 25%)" })
    }
};
const CloseButton = ({ onClick, style }) => (_jsx("button", { "aria-label": "Close Tooltip", onClick: onClick, onTouchStart: onClick, sx: Object.assign({ variant: "buttons.hollowLight", height: 11, width: 12 }, style), children: _jsxs("svg", { width: "12", height: "11", viewBox: "0 0 12 11", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M12 10.2857L7.33973 5.14286L12 0L9.80313 0L5.14286 5.14286L9.80313 10.2857H12Z", fill: "black" }), _jsx("path", { d: "M1.32911e-06 -9.53674e-07L4.66027 5.14286L0 10.2857H2.19687L6.85714 5.14286L2.19687 -9.53674e-07H1.32911e-06Z", fill: "black" })] }) }));
const calculateTooltipXOffset = (tooltipEl) => {
    const rect = tooltipEl.getBoundingClientRect();
    const offsetToMove = rect.width - TOOLTIP_ARROW_WIDTH;
    const paddingXOffset = window.outerWidth < 400 ? 8 : 16;
    const isLeftSideOutOfViewport = rect.left - rect.width / 2 < 0;
    const isRightSideOutOfViewport = rect.right + rect.width / 2 > window.outerWidth;
    let offsetX = offsetToMove / 2;
    if (isLeftSideOutOfViewport) {
        offsetX = rect.left - paddingXOffset;
    }
    else if (isRightSideOutOfViewport) {
        offsetX = rect.right - window.outerWidth + paddingXOffset;
    }
    return offsetX;
};
export const Tooltip = ({ id, title, description, customContent, styled = true, loading = false, children, onTooltipShow, onTooltipHide, position = "right", linkText = "Learn more", style = {}, link }) => {
    const wrapperRef = useRef(null);
    const vars = useRef({
        tooltipBoxOutsideViewport: false
    });
    const { current: v } = vars;
    const tooltipBoxRef = useCallback((node) => {
        // correct tooltip position when it renders outside of viewport
        if (node && v.tooltipBoxOutsideViewport) {
            node.style.left = `-${calculateTooltipXOffset(node)}px`;
        }
    }, []);
    const [tooltipState, setTooltipState] = useState({ position, show: false });
    const debouncedHandleMouseLeave = debounce(() => closeTooltip(), 200);
    const handleMouseLeave = (e) => {
        if (!tooltipState.show || isTouchDevice()) {
            return;
        }
        debouncedHandleMouseLeave();
    };
    const handleMouseEnter = (e) => {
        if (tooltipState.show || isTouchDevice()) {
            return;
        }
        debouncedHandleMouseLeave.cancel();
        let tooltipPosition = calculateTooltipPosition();
        //close other opened tooltips if we are opening a new one
        window.dispatchEvent(new CustomEvent(CLOSE_TOOLTIP_EVENT));
        setTooltipState({ show: true, position: tooltipPosition });
        if (onTooltipShow) {
            onTooltipShow();
        }
    };
    const calculateTooltipPosition = () => {
        let tooltipPosition = position;
        const el = wrapperRef.current;
        if (!el)
            return tooltipPosition;
        const { windowWidth } = getWindowDimensions();
        const { top: rectTopPos, left: rectXLeftPos, width: rectWidth } = el.getBoundingClientRect();
        const rectXRightPos = rectXLeftPos + rectWidth;
        const isRightSideOutOfViewport = rectXRightPos + TOOLTIP_ARROW_WIDTH + TOOLTIP_BOX_WIDTH + window.scrollX >
            windowWidth;
        const isLeftSideOutOfViewport = rectXLeftPos - TOOLTIP_ARROW_WIDTH - TOOLTIP_BOX_WIDTH + window.scrollX <
            0;
        v.tooltipBoxOutsideViewport = false;
        if (isRightSideOutOfViewport) {
            if (isLeftSideOutOfViewport) {
                tooltipPosition = rectTopPos < 300 ? "bottom" : "top";
                v.tooltipBoxOutsideViewport = true;
            }
            else {
                if (position !== "left") {
                    v.tooltipBoxOutsideViewport = true;
                }
                tooltipPosition = position !== "right" ? position : "left";
            }
        }
        else if (isLeftSideOutOfViewport) {
            tooltipPosition = position !== "left" ? position : "right";
        }
        return tooltipPosition;
    };
    const closeTooltip = () => {
        onTooltipHide && onTooltipHide();
        setTooltipState({ show: false, position: tooltipState.position });
    };
    useEffect(() => {
        window.addEventListener(CLOSE_TOOLTIP_EVENT, closeTooltip);
        return () => window.removeEventListener(CLOSE_TOOLTIP_EVENT, closeTooltip);
    }, []);
    const onTouchStart = (e) => {
        if (tooltipState.show) {
            closeTooltip();
        }
        else {
            //close other opened tooltips if we are opening a new one
            window.dispatchEvent(new CustomEvent(CLOSE_TOOLTIP_EVENT));
            let tooltipPosition = calculateTooltipPosition();
            setTooltipState({ show: !tooltipState.show, position: tooltipPosition });
            if (onTooltipShow)
                onTooltipShow();
        }
    };
    return (_jsxs(Box, { ref: wrapperRef, onMouseEnter: handleMouseEnter, onFocus: handleMouseEnter, onMouseLeave: handleMouseLeave, onBlur: handleMouseLeave, sx: {
            display: "inline-block",
            position: "relative"
        }, children: [_jsx(Box, { "aria-describedby": tooltipState.show ? id : undefined, tabIndex: 0, onTouchStart: onTouchStart, sx: styled
                    ? Object.assign(Object.assign(Object.assign({}, ThemeLinks[LinkVariants.standard]), { cursor: "default" }), style) : Object.assign({ cursor: "default" }, style), children: children }), tooltipState.show && (_jsxs(Box, { id: id, role: "tooltip", onMouseEnter: () => debouncedHandleMouseLeave.cancel(), onFocus: () => debouncedHandleMouseLeave.cancel(), children: [_jsx(Box, { sx: Object.assign({ position: "absolute", display: "block", overflow: "hidden", zIndex: 10000 }, TooltipArrowStyles[tooltipState.position]) }), _jsxs(Box, { ref: tooltipBoxRef, sx: Object.assign({ position: "absolute", display: "inline-block", zIndex: 9999, variant: `text.${TextVariants.label2}`, textAlign: "left", backgroundColor: "#FFFFFF", border: "1px solid", borderColor: `${Colors.grey}.3`, borderRadius: 6, width: [360, 360, TOOLTIP_BOX_WIDTH], height: "auto", padding: 24, boxShadow: "-1px 2px 2px -1px rgb(0 0 0 / 25%)" }, TooltipContainerStyles[tooltipState.position]), children: [loading && (_jsx(Spinner, { sx: { display: "block", margin: "0 auto", minHeight: 70 }, strokeWidth: 2, color: "inherit", size: 32 })), !loading && (_jsxs(_Fragment, { children: [title && _jsx(H6, { children: title }), customContent, _jsx("p", { sx: { mt: title ? 16 : 0, mb: link ? 16 : 0 }, children: description }), link && (_jsx("a", { role: "link", href: link, sx: { mt: 16, variant: `links.${LinkVariants.standard}` }, children: linkText }))] })), _jsx(CloseButton, { onClick: () => {
                                    debounce(() => closeTooltip(), 50)();
                                }, style: {
                                    position: "absolute",
                                    right: 16,
                                    top: 12,
                                    padding: 0
                                } })] })] }))] }));
};
export default Tooltip;
//# sourceMappingURL=Tooltip.js.map